import { snapVector } from '../grid';
import { point } from './point';
import type { Polygon } from './polygon';
import { Rectangle } from './rectangle';
import { type PositionVector, type Vector, vector } from './vector';

const keys = ['top', 'left', 'right', 'bottom'] as const;
export type BoundKey = (typeof keys)[number];
export type Bounds = {
  [key in BoundKey]: number;
};

const create = (
  left: number,
  top: number,
  right: number,
  bottom: number,
): Bounds => ({
  left,
  top,
  right,
  bottom,
});

const equals = (b1: Bounds, b2: Bounds): boolean =>
  !keys.some((key) => b1[key] !== b2[key]);

const fromPolygon = (s: Polygon): Bounds =>
  s.reduce(
    ({ left, top, right, bottom }, { x, y }) => ({
      left: Math.min(left, x),
      top: Math.min(top, y),
      right: Math.max(right, x),
      bottom: Math.max(bottom, y),
    }),
    {
      left: Number.POSITIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY,
    },
  );

/**
 * Get vector in the specified direction to move the boundsToMove overlapping with the staticBounds to get out of the overlap
 * When the bounds have no overlap at the initial position, it returns zero vector.
 */
const getVectorFromOverlapByVector = (
  boundsToMove: Bounds,
  staticBounds: Bounds,
  direction: Vector,
): Vector => {
  if (!doOverlap(boundsToMove, staticBounds)) {
    return vector.zero;
  }
  const requiredDeltaX =
    direction.x > 0
      ? staticBounds.right - boundsToMove.left
      : boundsToMove.right - staticBounds.left;
  const requiredDeltaY =
    direction.y > 0
      ? staticBounds.bottom - boundsToMove.top
      : boundsToMove.bottom - staticBounds.top;

  const xFactor = Math.abs(direction.x) / requiredDeltaX;
  const yFactor = Math.abs(direction.y) / requiredDeltaY;

  if (xFactor > yFactor) {
    return {
      x: Math.sign(direction.x) * requiredDeltaX,
      y:
        Math.abs(direction.y / direction.x) *
        requiredDeltaX *
        Math.sign(direction.y),
    };
  }
  return {
    x:
      Math.abs(direction.x / direction.y) *
      requiredDeltaY *
      Math.sign(direction.x),
    y: Math.sign(direction.y) * requiredDeltaY,
  };
};

/**
 * Get vector to free space for the bounds among the graphsBounds in the direction specified by the vector.
 * When the bounds have no overlap at the initial position, it returns zero vector.
 */
const getVectorToSpaceByVector = (
  graphBounds: Bounds,
  graphsBounds: Bounds[],
  vect: Vector,
  snapToGrid?: boolean,
): Vector => {
  const overlappedWith = graphsBounds.find((b) => doOverlap(graphBounds, b));

  if (vector.isZero(vect) || !overlappedWith) {
    return vector.fromCoords(0, 0);
  }

  let vectorOut = getVectorFromOverlapByVector(
    graphBounds,
    overlappedWith,
    vect,
  );
  if (snapToGrid) {
    vectorOut = snapVector(vectorOut);
  }

  // Do not try to filter graphsBounds (e.g. by the isBoundsInDirection()) - it just slows the calculation down
  const additionalVector = getVectorToSpaceByVector(
    move(graphBounds, vectorOut),
    graphsBounds,
    vect,
    snapToGrid,
  );

  return vector.add(vectorOut, additionalVector);
};

/**
 * Return true, if the bounds is in direction of the position vector, otherwise false.
 *
 * Please note, when the vector is positioned on the bounds edge or corner, but its direction is out of the bounds,
 * it returns false.
 */
const isBoundsInDirection = (
  bounds: Bounds,
  { point: { x, y }, vector: vect }: PositionVector,
): boolean =>
  (vect.x > 0 && bounds.right > x) ||
  (vect.x < 0 && bounds.left < x) ||
  (vect.y > 0 && bounds.bottom > y) ||
  (vect.y < 0 && bounds.top < y);

/**
 * Get true when there is a bounds overlap, otherwise return false;
 * The touch is not considered as overlap.
 *
 * Please note, this method works for 2D space where x grows left to right and y top to bottom (that is not usual in math)!
 */
const doOverlap = (bounds1: Bounds, bounds2: Bounds): boolean =>
  !(
    bounds1.left >= bounds2.right ||
    bounds2.left >= bounds1.right ||
    bounds1.top >= bounds2.bottom ||
    bounds2.top >= bounds1.bottom
  );

const move = (b: Bounds, delta: Vector): Bounds => ({
  left: b.left + delta.x,
  top: b.top + delta.y,
  right: b.right + delta.x,
  bottom: b.bottom + delta.y,
});

const resize = (bounds: Bounds, delta: number): Bounds => ({
  top: bounds.top - delta,
  left: bounds.left - delta,
  right: bounds.right + delta,
  bottom: bounds.bottom + delta,
});

const merge = (bounds0: Bounds, ...bounds: Bounds[]): Bounds =>
  bounds.length === 0
    ? bounds0
    : bounds.reduce(
        (result, current) =>
          keys.reduce((acc, key) => {
            if (Math.sign(current[key] - acc[key]) === sign[key]) {
              acc[key] = current[key];
            }
            return acc;
          }, result),
        { ...bounds0 },
      );

/**
 * Signs to help abstract operations on bound directions
 *
 * Top and left are negative because greater bound in these direction
 * is actually the lower number.
 */
const sign: { [key in BoundKey]: number } = {
  top: -1,
  left: -1,
  right: 1,
  bottom: 1,
};

const toRectangle = (bounds: Bounds): Rectangle =>
  new Rectangle(
    point.create(bounds.left, bounds.top),
    bounds.right - bounds.left,
    bounds.bottom - bounds.top,
  );

const zero: Bounds = {
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
};

export const bounds = {
  create,
  equals,
  fromPolygon,
  getVectorFromOverlapByVector,
  getVectorToSpaceByVector,
  isBoundsInDirection,
  doOverlap,
  keys,
  merge,
  move,
  resize,
  sign,
  toRectangle,
  zero,
};
